import { Injectable } from '@angular/core';
import { CanActivate, Router, UrlTree } from '@angular/router';
import { Observable, of, map, catchError } from 'rxjs';
import { AuthService } from '../services/auth.service';

@Injectable({
  providedIn: 'root',
})
export class LoginGuard implements CanActivate {
  private readonly RETURN_URL_KEY = 'auth_return_url';

  constructor(
    private readonly authService: AuthService,
    private readonly router: Router
  ) {}

  canActivate(): Observable<boolean | UrlTree> | Promise<boolean | UrlTree> | boolean | UrlTree {
    const token = this.authService.getToken();
    const refreshToken = this.authService.getRefreshToken();
    const isTokenExpired = token ? this.authService.isTokenExpired() : true;

    // If token exists and is valid, redirect to dashboard
    if (token && !isTokenExpired) {
      return this.redirectToDashboard();
    }

    // If token is expired but refresh token exists, try to refresh
    if (isTokenExpired && refreshToken) {
      return this.authService.refreshToken().pipe(
        map(() => this.redirectToDashboard()),
        catchError(() => {
          // Refresh failed, allow access to login page
          return of(true);
        })
      );
    }

    // User is not authenticated, allow access to login page
    return true;
  }

  private redirectToDashboard(): UrlTree {
    const returnUrl = this.getStoredReturnUrl() ?? '/dashboard';
    this.clearStoredReturnUrl();
    return this.router.createUrlTree([returnUrl]);
  }

  private getStoredReturnUrl(): string | null {
    // Check sessionStorage first (used by auth service)
    const sessionUrl = sessionStorage.getItem(this.RETURN_URL_KEY);
    if (sessionUrl) {
      return sessionUrl;
    }

    // Fallback to localStorage
    const localUrl = localStorage.getItem(this.RETURN_URL_KEY);
    if (localUrl) {
      return localUrl;
    }

    return null;
  }

  private clearStoredReturnUrl(): void {
    sessionStorage.removeItem(this.RETURN_URL_KEY);
    localStorage.removeItem(this.RETURN_URL_KEY);
  }
}
